import React from 'react';
import { ColumnFormSelect } from './FormSelect.js';
import { TextField, FormControl } from '@mui/material';
import { outlinedInputSX } from './FormSelect.js';

/**
 * Generates an array of DataGrid columns to be used in the DriverRouteMessaging component.
 * 
 * @param {Array} vehicles - An array of vehicle numbers.
 * @param {Array} drivers - An array of driver objects with Name and Phone properties.
 * @param {Function} handleVehicleChange - A function to call when the vehicle changes. It takes an event object and the row id and route time as arguments.
 * @param {Function} handleDriverChange - A function to call when the driver changes. It takes an event object, the row id, and route time as arguments.
 * @param {Function} handleMessageBody - A function to call when the message body changes. It takes an event object and the row as arguments.
 * @param {Set} assignedDriverNames - A set of driver names that have already been assigned to a row.
 * @returns {Array} - An array of DataGrid columns.
 * 
 * The columns generated are:
 * - Vehicle # (vehicleNo): A select field that allows the user to select a vehicle from the list of vehicles provided.
 * - Driver Name (driver): A select field that allows the user to select a driver from the list of drivers provided. If the driver has already been assigned to a row, it will not be available for selection.
 * - Phone # (phone): A text field to enter the phone number.
 * - Email (email): A text field to enter the email.
 * - Route (id): The route id (not editable).
 * - Route Start (routeTime): The start time of the route (editable).
 * - Message Body (sms): A text area to enter the message body. The message body will be rendered as a read-only text area if the row is not selected.
 * - Response (response): A read-only text field that displays the response from the driver.
 */

const generateColumns = (vehicles, drivers, handleVehicleChange, handleDriverChange, handleMessageBody, assignedDriverNames) => {
  return [
    { 
      field: 'vehicleNo', 
      headerName: 'Vehicle #', 
      width: 150, 
      align: 'center',
      cellClassName: 'superCentered',
      renderCell: (params) => {
        const rowId = params.row.id;
        const routeTime = params.row.routeTime;
        return (
          <ColumnFormSelect
            value={params.row.vehicleNo}
            onChange={e => handleVehicleChange(e, params.row.id, params.row.routeTime)}
            options={vehicles.current}
            getOptionKey={v => v}
            getOptionValue={v => v}
            getOptionLabel={v => v.toString()}
            sx={{ m: 0, width: 130 }}
          />
        );
      },
    },
    {
      field: 'driver',
      headerName: 'Driver Name',
      width: 250,
      align: 'center',
      cellClassName: 'superCentered',
      renderCell: (params) => {
        const rowId = params.row.id;
        const routeTime = params.row.routeTime;
        const availableDrivers = drivers.current.filter(d =>
          d.Name === params.row.driver || !assignedDriverNames.has(d.Name)
        );
        return (
          <ColumnFormSelect
            value={params.row.driver}
            onChange={e => handleDriverChange(e, rowId, routeTime)}
            options={availableDrivers}
            getOptionKey={d => d.Name}
            getOptionValue={d => d.Name}
            getOptionLabel={d => d.Name}
            sx={{ m: 0, width: 230 }}
          />
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone #',
      width: 150,
      editable: true,
      align: 'center',
      cellClassName: 'superCentered'
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 150,
      editable: true,
      align: 'center',
      cellClassName: 'superCentered'
    },
    {
      field: 'id',
      headerName: 'Route',
      width: 180,
      editable: false,
      align: 'center',
      cellClassName: 'superCentered'
    },
    {
      field: 'routeTime',
      headerName: 'Route Start',
      width: 110,
      editable: true,
      align: 'center',
      cellClassName: 'superCentered'
    },
    {
      field: 'sms',
      headerName: 'Message Body',
      width: 550,
      align: 'center',
      cellClassName: 'superCentered',
      renderCell: (params) => {
        const row = params.row;
        return (
          <FormControl variant="outlined" sx={{ marginTop: 1, marginBottom: 1, p: 0, width: '100%' }}>
            <TextField 
              label="Enter Message" 
              size='small' 
              multiline
              minRows={3}
              maxRows={7}
              value={row.sms}
              onChange={(e) => handleMessageBody(e, row)}
              onKeyDown={e => { if (e.key === ' ') e.stopPropagation(); }}
              sx={{ ...outlinedInputSX }}
            />
          </FormControl>
        );
      },
    },
    {
      field: 'response',
      headerName: 'Response',
      width: 280,
      editable: false,
      align: 'center',
      cellClassName: (params) => {
        const negativeKeywords = ['no', 'not available', 'unavailable', 'can\'t', 'sorry', 'i refuse', 'i disagree', 'not possible'];
        const positiveKeywords = ['yes', 'confirm', 'sure', 'okay', 'alright', 'got it', 'understood', 'i\'ll be there', 'affirmative', "available"];
        const response = params.row.response.toLowerCase();
        if (negativeKeywords.some(keyword => response.toLowerCase().includes(keyword))) {
          return 'super-app negative'; // Negative response
        }
        else if (positiveKeywords.some(keyword => response.toLowerCase().includes(keyword))) {
          return 'super-app positive'; // Positive response
        }
        else if (response !== '') {
          return 'super-app unknown'; // Unknown
        }
        return 'super-app'; // Default
      },
      renderCell: (params) => {
        return (
          <div className="superCentered">
            {params.row.response}
          </div>
        );
      },
    }
  ];
}

export default generateColumns;
